// Read-only presentation. Item outcomes are separate from procurement success,
// payment settlement, and delivery. No API or monetary values are replaced.
export const PROGRESS_STATUSES = ['DAPAT', 'ORDER', 'SOLD', 'CANCEL', 'RETUR'];
const resolvedStatuses = new Set(['DAPAT', 'SOLD', 'CANCEL']);
const readyFees = new Set(['charged', 'free', 'fully_subsidized']);
const feeReadyStatuses = new Set(['SIAP_TAGIH', 'SIAP_KIRIM', 'TERKIRIM']);
const normalized = value => typeof value === 'string' ? value.trim().toUpperCase() : '';
const nonnegativeInteger = value => Number.isSafeInteger(value) && value >= 0;
const progressStatus = value => {
  const status = normalized(value);
  const aliases = { 'SOLD OUT': 'SOLD', SOLD_OUT: 'SOLD', CANCELLED: 'CANCEL', BATAL: 'CANCEL' };
  return Object.prototype.hasOwnProperty.call(aliases, status) ? aliases[status] : status;
};

export function orderProgress(orders, summary) {
  const counts = { DAPAT: 0, ORDER: 0, SOLD: 0, CANCEL: 0, RETUR: 0, UNKNOWN: 0 };
  let total = 0, resolved = 0, valid = Array.isArray(orders);
  (Array.isArray(orders) ? Array.from(orders) : []).forEach(item => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) { valid = false; return; }
    const quantity = item.qty;
    if (item.quantity_valid !== true || !Number.isSafeInteger(quantity) || quantity <= 0) {
      valid = false;
      return;
    }
    // A return needs follow-up unless the API explicitly provides a final outcome.
    // Unknown values must never inherit a resolved status through prefix matching.
    const hasRawStatus = Object.prototype.hasOwnProperty.call(item, 'raw_status');
    const rawStatus = progressStatus(item.raw_status);
    const statusValue = progressStatus(item.status);
    const status = hasRawStatus && rawStatus !== statusValue ? 'UNKNOWN' : hasRawStatus ? rawStatus : statusValue;
    const key = PROGRESS_STATUSES.includes(status) ? status : 'UNKNOWN';
    counts[key] += quantity;
    total += quantity;
    if (resolvedStatuses.has(key)) resolved += quantity;
    if (!Number.isSafeInteger(total)) valid = false;
  });
  valid = valid && nonnegativeInteger(summary?.total_pcs) && summary.total_pcs === total &&
    nonnegativeInteger(summary?.total_dapat) && summary.total_dapat === counts.DAPAT;
  const complete = valid && total > 0 && resolved === total;
  const percent = valid && total > 0 ? (complete ? 100 : Math.floor(resolved / total * 100)) : null;
  const label = !valid ? 'Progres pesanan belum tersedia'
    : total === 0 ? 'Belum ada barang di pesanan ini'
    : complete ? 'Pesanan selesai diproses'
    : `${resolved} dari ${total} barang selesai diproses`;
  return { counts, total, resolved, valid, complete, percent, label };
}

export function canOmitFutureBillCaveat(data, progress, balanceState) {
  if (!['CLOSED', 'CLOSE'].includes(normalized(data?.po?.status)) || !progress.complete ||
      !['paid', 'underpaid', 'overpaid'].includes(balanceState) || data.tracking_available !== true ||
      !Array.isArray(data.packages) || !readyFees.has(data.shipping_summary?.fee_state)) return false;
  // The summary alone is insufficient: a second package or unknown activity
  // must not hide a fee that is still pending.
  const packages = Array.from(data.packages);
  const active = packages.filter(pkg => pkg?.active === true);
  if (active.length === 0 || data.shipping_summary.active_package_count !== active.length) return false;
  return packages.every(pkg => pkg && (
    pkg.active === true ? feeReadyStatuses.has(normalized(pkg.status)) && readyFees.has(pkg.shipping_fee_state) &&
      nonnegativeInteger(pkg.shipping_net) && (pkg.shipping_fee_state === 'charged' ? pkg.shipping_net > 0 : pkg.shipping_net === 0)
      : pkg.active === false && normalized(pkg.status) === 'DIBATALKAN'
  )) && Number.isSafeInteger(active.reduce((sum, pkg) => sum + pkg.shipping_net, 0)) &&
    active.reduce((sum, pkg) => sum + pkg.shipping_net, 0) === data.summary?.ongkir;
}
